/**
 * Navigation Card — hide sidebar sections this org never uses.
 *
 * ⚠ DECLUTTERING, NOT ACCESS CONTROL. Hiding a section removes the nav entry
 * and nothing else: its routes stay reachable by URL and its endpoints keep
 * serving. Anything that must actually be denied belongs in RBAC
 * (`apps/backend/src/lib/rbac.ts`). The card says so on screen, because a
 * settings toggle that *looks* like a lock is worse than no toggle — an
 * operator would stop asking for the real one.
 *
 * Why it exists: a storeless 3PL owner org (Thread Lab) runs a production
 * floor and purchases nothing, so Ordering and Stock & Ops are permanently
 * empty chrome; its clients' work arrives through the partner portal instead.
 *
 * Self-contained: reads org settings from context, writes the whole settings
 * blob through updateOrganization — the same spread-and-merge the Brands card
 * uses, so a save here never drops a sibling settings block.
 */

import { useMemo, useState } from 'react';
import { PanelLeft, Save, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  HIDEABLE_NAV_SECTIONS,
  HIDEABLE_NAV_SECTION_LABEL_KEYS,
  type HideableNavSection,
} from '@threadheads/shared';
import type { UpdateOrganizationInput } from '@threadheads/shared';

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useRole } from '@/hooks/use-role';
import { useOrganizationMutations } from '../hooks/useOrganizationMutations';

export function NavigationSettingsCard() {
  const { t } = useTranslation('common');
  const { organization } = useOrganization();
  const { isAdmin } = useRole();
  const { updateOrganization } = useOrganizationMutations();

  const saved = useMemo<HideableNavSection[]>(
    () => organization?.settings?.hiddenNavSections ?? [],
    [organization?.settings?.hiddenNavSections],
  );
  const [hidden, setHidden] = useState<HideableNavSection[]>(saved);
  const [dirty, setDirty] = useState(false);

  // The switch reads as "show this section", not "hide it" — a checked box
  // meaning "hidden" inverts what every other toggle on this page means.
  const isShown = (id: HideableNavSection) => !hidden.includes(id);

  const toggle = (id: HideableNavSection, show: boolean) => {
    setHidden((prev) => (show ? prev.filter((s) => s !== id) : [...new Set([...prev, id])]));
    setDirty(true);
  };

  const handleSave = () => {
    const currentSettings = organization?.settings ?? {};
    updateOrganization.mutate(
      { settings: { ...currentSettings, hiddenNavSections: hidden } } as unknown as UpdateOrganizationInput,
      { onSuccess: () => setDirty(false) },
    );
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <PanelLeft className="h-4 w-4 text-muted-foreground" />
          {t('settings.navigation.title', 'Navigation')}
        </CardTitle>
        <CardDescription>
          {t(
            'settings.navigation.description',
            'Hide sidebar sections your team never uses. This only tidies the menu; it does not restrict access, and hidden pages stay reachable by their direct link.',
          )}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-3">
          {HIDEABLE_NAV_SECTIONS.map((id) => (
            <div key={id} className="flex items-center justify-between gap-4">
              <Label htmlFor={`nav-section-${id}`} className="text-sm font-normal">
                {t(HIDEABLE_NAV_SECTION_LABEL_KEYS[id], id)}
              </Label>
              <Switch
                id={`nav-section-${id}`}
                checked={isShown(id)}
                disabled={!isAdmin()}
                onCheckedChange={(show) => toggle(id, show)}
              />
            </div>
          ))}
        </div>

        {isAdmin() && (
          <div className="flex justify-end pt-1">
            <Button
              type="button"
              size="sm"
              onClick={handleSave}
              disabled={!dirty || updateOrganization.isPending}
            >
              {updateOrganization.isPending ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              {t('actions.save', 'Save')}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
